import { useState, useEffect } from 'react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Package, Search, Pause, Trash2, Shield, Snowflake, Eye, ChevronLeft, ChevronRight, Loader2, ExternalLink } from 'lucide-react';

const STATUS_COLORS = {
  active: 'bg-emerald-500/10 text-emerald-400', paused_admin: 'bg-amber-500/10 text-amber-400',
  paused_billing: 'bg-orange-500/10 text-orange-400', removed: 'bg-red-500/10 text-red-400',
  frozen: 'bg-blue-500/10 text-blue-400', compliance_review: 'bg-purple-500/10 text-purple-400',
  draft: 'bg-zinc-500/10 text-zinc-400', sold: 'bg-teal-500/10 text-teal-400',
};

export default function AdminModerationPage() {
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [demoFilter, setDemoFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null);
  const [reason, setReason] = useState('');

  useEffect(() => { load(); }, [page, statusFilter, demoFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.adminGetListings({ page, limit: 20, status: statusFilter !== 'all' ? statusFilter : undefined, search: search || undefined, is_demo: demoFilter !== 'all' ? demoFilter : undefined });
      setListings(data.listings || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); load(); };

  const handleStatusChange = async (listingId, newStatus) => {
    try {
      await api.adminUpdateListingStatus(listingId, { status: newStatus, reason });
      toast.success(`Listing actualizado a ${newStatus}`);
      setActionDialog(null); setReason(''); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('¿Eliminar este listing permanentemente?')) return;
    try {
      await api.adminDeleteListing(listingId, { reason: 'Admin deletion' });
      toast.success('Listing eliminado');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleCleanupDemos = async () => {
    const demoCount = listings.filter(l => l.is_demo).length;
    if (!window.confirm(`¿Eliminar ${demoCount > 0 ? '' : 'todas las '}publicaciones demo?`)) return;
    try {
      const res = await api.cleanupDemos();
      toast.success(res.message);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="admin-moderation-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 mb-1">Moderacion de Publicaciones</h1>
          <p className="text-xs text-zinc-600">{total} publicaciones</p>
        </div>
        <Button variant="outline" className="border-red-500/20 text-red-400 text-xs" onClick={handleCleanupDemos} data-testid="cleanup-demos-btn">
          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Limpiar demos
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
            <Input placeholder="Buscar por titulo..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-white/[0.03] border-white/5 text-zinc-100 h-9 text-sm" data-testid="moderation-search" />
          </div>
        </form>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 bg-white/[0.03] border-white/5 text-zinc-300 h-9 text-sm"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Activo</SelectItem>
            <SelectItem value="paused_admin">Pausado admin</SelectItem>
            <SelectItem value="frozen">Congelado</SelectItem>
            <SelectItem value="compliance_review">Compliance</SelectItem>
            <SelectItem value="removed">Eliminado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={demoFilter} onValueChange={v => { setDemoFilter(v); setPage(1); }}>
          <SelectTrigger className="w-32 bg-white/[0.03] border-white/5 text-zinc-300 h-9 text-sm"><SelectValue placeholder="Demo" /></SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="true">Solo demo</SelectItem>
            <SelectItem value="false">Solo reales</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Listings Grid */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12 text-zinc-600 text-sm">Sin publicaciones</div>
        ) : listings.map(l => (
          <div key={l.id} className="flex items-center gap-4 p-3 rounded-xl border border-white/[0.04] bg-[#0A0A0D] hover:bg-white/[0.01] transition-colors" data-testid={`listing-row-${l.id}`}>
            {/* Thumbnail */}
            <div className="w-12 h-12 rounded-lg bg-zinc-900 flex-shrink-0 overflow-hidden relative">
              {l.images?.[0] ? <img src={l.images[0]} alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-zinc-700 m-auto" />}
              {l.is_demo && <span className="absolute top-0.5 right-0.5 text-[8px] bg-amber-500 text-black px-1 rounded font-bold">DEMO</span>}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-xs text-zinc-200 font-medium truncate">{l.title}</p>
              <p className="text-[10px] text-zinc-600 truncate">{l.seller_name || 'Unknown'} - ${l.price?.toLocaleString('es-MX')} MXN</p>
            </div>

            {/* Status */}
            <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${STATUS_COLORS[l.status] || STATUS_COLORS.draft}`}>
              {l.status}
            </span>

            {/* Actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {l.status === 'active' && (
                <>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-400 hover:bg-amber-500/10"
                    onClick={() => handleStatusChange(l.id, 'paused_admin')} title="Pausar">
                    <Pause className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-400 hover:bg-blue-500/10"
                    onClick={() => handleStatusChange(l.id, 'frozen')} title="Congelar">
                    <Snowflake className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-purple-400 hover:bg-purple-500/10"
                    onClick={() => handleStatusChange(l.id, 'compliance_review')} title="Compliance">
                    <Shield className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
              {['paused_admin', 'frozen', 'compliance_review'].includes(l.status) && (
                <Button size="sm" variant="ghost" className="h-7 text-[10px] text-emerald-400 hover:bg-emerald-500/10"
                  onClick={() => handleStatusChange(l.id, 'active')}>
                  Reactivar
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/10"
                onClick={() => handleDelete(l.id)} title="Eliminar">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-zinc-600">Pagina {page} de {Math.ceil(total / 20)}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs text-zinc-400">
              <ChevronLeft className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" disabled={listings.length < 20} onClick={() => setPage(p => p + 1)} className="h-7 text-xs text-zinc-400">
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
